import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { useForm, Controller } from "react-hook-form";
import anchorme from "anchorme";

import { useCommunity } from "domain/community/communityContext";
import { useTheme } from "lib/contexts/themeContext";
import { useLayout } from "lib/contexts/layoutContext";
import ReportModal from "lib/components/ReportModal";
import Avatar from "lib/components/Avatar";
import Button from "lib/components/Button";
import TextField from "lib/components/TextField";
import { Menu, MenuItem } from "lib/components/Menu";
import { fromNowRecent } from "lib/date";
import IconExclamation from "lib/assets/exclamation.svg";
import IconEdit from "lib/assets/edit.svg";
import IconDelete from "lib/assets/trash.svg";
import { useAuth } from "domain/auth/authContext";
import IconCertified from "lib/assets/badge-certified.svg";
import { purify } from "lib/format";

interface CommentProps {
  id: number;
  name: string;
  contents: string;
  createdAt: string;
  isMine: boolean;
  onDelete: (commentId: number) => void;
  onUpdate: () => Promise<void>;
  profileImage?: string;
  className?: string;
  isUserCertified?: boolean;
}

const Comment = React.memo<CommentProps>(
  ({
    id,
    name,
    contents,
    createdAt,
    isMine,
    onDelete,
    onUpdate,
    profileImage,
    className,
    isUserCertified,
  }) => {
    const theme = useTheme();
    const {
      state: { dataUser },
    } = useAuth();
    const { doOpenAlert } = useLayout();
    const { doReportComment } = useCommunity();
    const [isEditing, setIsEditing] = useState(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);

    const handleOnEditClick = useCallback(() => {
      setIsEditing(true);
    }, []);

    const handleOnReportClick = useCallback(() => {
      setIsReportModalOpen(true);
    }, []);

    const handleOnEditClose = useCallback(() => {
      setIsEditing(false);
    }, []);

    const handleOnReportModalClose = useCallback(() => {
      setIsReportModalOpen(false);
    }, []);

    const handleOnDeleteClick = useCallback(() => {
      doOpenAlert({
        content: "Are you sure you want to delete it?",
        confirmMessage: "Delete",
        cancelMessage: "Cancel",
        onConfirm: () => onDelete(id),
        onCancel: () => {
          // noop
        },
      });
    }, [doOpenAlert, id, onDelete]);

    const handleOnReport = useCallback(
      async (data: { title: string; contents: string }) => {
        try {
          await doReportComment({ commentId: id, ...data });
          handleOnReportModalClose();
        } catch (e) {
          doOpenAlert({
            content: "Report unsuccessful. Please try again shortly.",
            isError: true,
          });
        }
      },
      [doOpenAlert, doReportComment, handleOnReportModalClose, id],
    );

    const contentsAnchoredAndPurified = anchorme({
      input: purify(contents),
      options: {
        attributes: {
          target: "_blank",
          rel: "noopener",
        },
      },
    });

    return (
      <div
        css={`
          display: flex;
        `}
        className={className}
      >
        <Avatar
          css={`
            margin-right: 10px;
            width: 42px;
          `}
          src={profileImage}
          letter={name[0]}
        />
        <div
          css={`
            flex: 1;
          `}
        >
          <div
            css={`
              margin-bottom: 2px;
              display: flex;
            `}
          >
            <div
              css={`
                flex: 1;
                margin-right: 5px;
                line-height: 20px;
                display: flex;
                align-items: center;
              `}
            >
              <span
                css={`
                  padding-right: 4px;
                  font-size: 16px;
                  line-height: 24px;
                  font-weight: 500;
                  word-break: break-word;
                `}
              >
                {name}
              </span>
              {isUserCertified && (
                <IconCertified
                  css={`
                    margin-right: 5px;
                  `}
                />
              )}
              <span
                css={`
                  font-size: 12px;
                  color: ${theme.palette.grey[500]};
                `}
              >
                {fromNowRecent(createdAt)}
              </span>
            </div>

            {!!dataUser && (
              <Menu>
                {isMine ? (
                  <>
                    <MenuItem icon={<IconEdit />} onClick={handleOnEditClick}>
                      Edit
                    </MenuItem>
                    <MenuItem
                      icon={<IconDelete />}
                      onClick={handleOnDeleteClick}
                    >
                      Delete
                    </MenuItem>
                  </>
                ) : (
                  <MenuItem
                    icon={<IconExclamation />}
                    onClick={handleOnReportClick}
                  >
                    Report
                  </MenuItem>
                )}
              </Menu>
            )}
          </div>

          {isEditing ? (
            <EditComment
              id={id}
              comment={contents}
              onCancel={handleOnEditClose}
              onEdit={onUpdate}
            />
          ) : (
            <div
              css={`
                white-space: pre-line;
                line-height: 24px;
                font-size: 16px;
                overflow-wrap: break-word;
                word-wrap: break-word;
                word-break: break-word;
                hyphens: auto;
              `}
              dangerouslySetInnerHTML={{
                __html: contentsAnchoredAndPurified,
              }}
            />
          )}
        </div>

        <ReportModal
          open={isReportModalOpen}
          onSubmit={handleOnReport}
          onClose={handleOnReportModalClose}
        />
      </div>
    );
  },
);

export default Comment;

interface EditCommentProps {
  id: number;
  comment: string;
  onEdit: () => Promise<void>;
  onCancel: () => void;
}

const EditComment: React.FC<EditCommentProps> = ({
  id,
  comment,
  onEdit,
  onCancel,
}) => {
  const { control, errors, formState, getValues, handleSubmit } = useForm<{
    comment: string;
  }>({ defaultValues: { comment } });
  const { isSubmitting } = formState;
  const { doEditComment } = useCommunity();
  const validate = useCallback(
    () => getValues().comment.trim().length > 0,
    [getValues],
  );

  const handleOnSubmit = handleSubmit(async (data) => {
    try {
      await doEditComment({
        commentId: id,
        comment: purify(data.comment),
      });
      await onEdit();
      onCancel();
    } catch (e) {
      // noop
    }
  });

  return (
    <form onSubmit={handleOnSubmit}>
      <div
        css={`
          margin-bottom: 10px;
        `}
      >
        <Controller
          as={TextField}
          control={control}
          name="comment"
          type="text"
          placeholder="Write a Comment"
          autoFocus
          fullWidth
          multiline
          maxRows={5}
          disabled={isSubmitting}
          rules={{ validate }}
          error={!!errors.comment}
          helperText={errors.comment?.message}
        />
      </div>
      <div
        css={`
          display: flex;
          justify-content: flex-end;
        `}
      >
        <div
          css={`
            margin-right: 10px;
            width: 80px;
          `}
        >
          <StyledButton size="small" round neutral onClick={onCancel}>
            Cancel
          </StyledButton>
        </div>

        <div
          css={`
            width: 83px;
          `}
        >
          <StyledButton
            type="submit"
            size="small"
            round
            disabled={isSubmitting}
          >
            Update
          </StyledButton>
        </div>
      </div>
    </form>
  );
};

const StyledButton = styled(Button)`
  &&& {
    font-weight: 500;
  }
`;
